import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { toast } from "sonner";
import { 
  Upload, ArrowRightLeft, Trash2, Eye, Download, 
  Loader2, FileSpreadsheet, History, ChevronDown, ChevronUp
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompareToolTab = () => {
  const [sheets, setSheets] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [selectedSheets, setSelectedSheets] = useState([]);
  const [comparisonType, setComparisonType] = useState("duplicates");
  const [showHistory, setShowHistory] = useState(false);
  const [viewingResult, setViewingResult] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [sheetsRes, resultsRes] = await Promise.all([
        fetch(`${API}/compare-tool/sheets`),
        fetch(`${API}/compare-tool/results`)
      ]);
      if (sheetsRes.ok) setSheets(await sheetsRes.json());
      if (resultsRes.ok) setResults(await resultsRes.json());
    } catch (error) {
      console.error("Error loading data:", error);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      try {
        const response = await fetch(`${API}/compare-tool/sheets/upload`, {
          method: "POST",
          body: formData,
        });
        if (response.ok) {
          const data = await response.json();
          toast.success(`${file.name}: ${data.entry_count} URLs cargadas`);
        } else {
          const error = await response.json();
          toast.error(error.detail || "Error al subir archivo");
        }
      } catch (error) {
        toast.error(`Error subiendo ${file.name}`);
      }
    }
    loadData();
    setUploading(false);
    e.target.value = "";
  };

  const handleCompare = async () => {
    if (selectedSheets.length !== 2) {
      toast.error("Selecciona exactamente 2 archivos");
      return;
    }
    
    setComparing(true);
    try {
      const response = await fetch(`${API}/compare-tool/compare`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sheet_ids: selectedSheets,
          comparison_type: comparisonType,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Comparación completada: ${data.result_count} URLs encontradas`);
        setSelectedSheets([]);
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error en la comparación");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
    setComparing(false);
  };

  const deleteSheet = async (sheetId, e) => {
    e.stopPropagation();
    try {
      await fetch(`${API}/compare-tool/sheets/${sheetId}`, {
        method: "DELETE",
      });
      toast.success("Archivo eliminado");
      loadData();
      setSelectedSheets(prev => prev.filter(id => id !== sheetId));
    } catch (error) {
      toast.error("Error eliminando archivo");
    }
  };

  const deleteResult = async (resultId) => {
    try {
      await fetch(`${API}/compare-tool/results/${resultId}`, {
        method: "DELETE",
      });
      toast.success("Resultado eliminado");
      loadData();
      if (viewingResult?.result_id === resultId) {
        setViewingResult(null);
      }
    } catch (error) {
      toast.error("Error eliminando resultado");
    }
  };

  const viewResult = async (resultId) => {
    try {
      const response = await fetch(`${API}/compare-tool/results/${resultId}`);
      if (response.ok) {
        const data = await response.json();
        setViewingResult(data);
      }
    } catch (error) {
      toast.error("Error cargando resultado");
    }
  };

  const downloadExcel = async (resultId, name) => {
    try {
      const response = await fetch(`${API}/compare-tool/results/${resultId}/export-excel`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${name}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Excel descargado");
      }
    } catch (error) {
      toast.error("Error al descargar");
    }
  };

  const toggleSheetSelection = (sheetId) => {
    setSelectedSheets(prev => {
      if (prev.includes(sheetId)) {
        return prev.filter(id => id !== sheetId);
      }
      if (prev.length >= 2) {
        toast.error("Solo puedes seleccionar 2 archivos");
        return prev;
      }
      return [...prev, sheetId];
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // If viewing a result
  if (viewingResult) {
    return (
      <div className="space-y-6" data-testid="compare-result-view">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">{viewingResult.name}</h3>
            <p className="text-sm text-muted-foreground">
              {viewingResult.result_count} URLs • {viewingResult.comparison_type}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => downloadExcel(viewingResult.result_id, viewingResult.name)}>
              <Download className="w-4 h-4 mr-2" />
              Descargar Excel
            </Button>
            <Button variant="outline" size="sm" onClick={() => setViewingResult(null)}>
              Volver
            </Button>
          </div>
        </div>
        
        <Card>
          <CardContent className="pt-6">
            <div className="max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>URL</TableHead>
                    <TableHead className="text-right">Precio</TableHead>
                    <TableHead>Email</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {viewingResult.entries?.slice(0, 100).map((entry, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-mono text-sm text-blue-600 truncate max-w-md">
                        <a href={entry.url.startsWith('http') ? entry.url : `https://${entry.url}`} target="_blank" rel="noopener noreferrer" className="hover:underline">
                          {entry.url}
                        </a>
                      </TableCell>
                      <TableCell className="text-right">{entry.precio ? `${entry.precio}€` : "-"}</TableCell>
                      <TableCell className="truncate max-w-xs">{entry.email || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {viewingResult.entries?.length > 100 && (
              <p className="text-sm text-muted-foreground text-center mt-4">
                Mostrando 100 de {viewingResult.entries.length} resultados. Descarga el Excel para ver todos.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="compare-tool-tab">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
          <ArrowRightLeft className="w-5 h-5 text-blue-500" />
        </div>
        <div>
          <h2 className="text-xl font-bold">Comparar Archivos</h2>
          <p className="text-sm text-muted-foreground">Encuentra duplicados, únicos o excluye URLs entre archivos CSV/Excel</p>
        </div>
      </div>

      {/* Upload */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Subir Archivos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div 
            className="border-2 border-dashed border-border/60 rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
            onClick={() => document.getElementById("compare-file-upload")?.click()}
          >
            <FileSpreadsheet className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {uploading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Subiendo...
                </span>
              ) : (
                "Haz clic o arrastra archivos CSV o Excel aquí"
              )}
            </p>
            <input
              id="compare-file-upload"
              type="file"
              className="hidden"
              accept=".csv,.xlsx,.xls"
              multiple
              onChange={handleFileUpload}
              disabled={uploading}
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Files List */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Archivos ({sheets.length})</CardTitle>
            <CardDescription>Selecciona 2 archivos para comparar</CardDescription>
          </CardHeader>
          <CardContent>
            {sheets.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No hay archivos subidos
              </p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {sheets.map((sheet) => (
                  <div
                    key={sheet.sheet_id}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                      selectedSheets.includes(sheet.sheet_id)
                        ? "border-primary bg-primary/5"
                        : "border-border/50 hover:border-border"
                    }`}
                    onClick={() => toggleSheetSelection(sheet.sheet_id)}
                  >
                    <Checkbox checked={selectedSheets.includes(sheet.sheet_id)} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{sheet.filename}</p>
                      <p className="text-xs text-muted-foreground">{sheet.entry_count} URLs</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => deleteSheet(sheet.sheet_id, e)}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Compare Options */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Comparar</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Tipo de comparación</label>
              <Select value={comparisonType} onValueChange={setComparisonType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="duplicates">URLs duplicadas (en ambos)</SelectItem>
                  <SelectItem value="unique_first">URLs únicas del archivo 1</SelectItem>
                  <SelectItem value="unique_second">URLs únicas del archivo 2</SelectItem>
                  <SelectItem value="exclude">Excluir archivo 2 del 1</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="p-3 bg-muted/30 rounded-lg text-xs space-y-1">
              <p><strong>Duplicadas:</strong> URLs que están en ambos archivos</p>
              <p><strong>Únicas:</strong> URLs que solo están en un archivo</p>
              <p><strong>Excluir:</strong> Quita del archivo 1 las URLs del archivo 2</p>
            </div>

            {selectedSheets.length === 2 && (
              <div className="p-3 bg-blue-500/10 rounded-lg text-sm">
                <p className="font-medium text-blue-600">Archivos seleccionados:</p>
                <p className="text-xs mt-1">
                  1. {sheets.find(s => s.sheet_id === selectedSheets[0])?.filename}
                </p>
                <p className="text-xs">
                  2. {sheets.find(s => s.sheet_id === selectedSheets[1])?.filename}
                </p>
              </div>
            )}

            <Button 
              className="w-full" 
              onClick={handleCompare}
              disabled={selectedSheets.length !== 2 || comparing}
            >
              {comparing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <ArrowRightLeft className="w-4 h-4 mr-2" />
              )}
              Comparar Archivos
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Results History */}
      <Card>
        <CardHeader 
          className="pb-3 cursor-pointer"
          onClick={() => setShowHistory(!showHistory)}
        >
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <History className="w-4 h-4" />
              Historial de Comparaciones ({results.length})
            </span>
            {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </CardTitle>
        </CardHeader>
        {showHistory && (
          <CardContent>
            {results.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No hay comparaciones realizadas
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Resultados</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.slice(0, 10).map((result) => (
                    <TableRow key={result.result_id}>
                      <TableCell className="font-medium truncate max-w-xs">{result.name}</TableCell>
                      <TableCell className="text-xs">{result.comparison_type}</TableCell>
                      <TableCell className="text-right">{result.result_count}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => viewResult(result.result_id)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => downloadExcel(result.result_id, result.name)}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteResult(result.result_id)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default CompareToolTab;
